// Custom hook for incident form logic
// CREATED: Extracts shared logic from IncidentFormModal and IncidentBottomSheet
// UPDATED: After successful incident creation, fires send-incident-notification edge function (fire-and-forget)
// Features:
// - Form state management (category, description, site selection, file attachments)
// - Form validation
// - Site loading and auto-selection
// - Form submission with error handling and file uploads
// - Dirty form tracking for unsaved changes detection (includes site selection for multi-site users and file attachments)
// - Form reset on open/close

import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { createIncident } from '../lib/incidents';
import { sendIncidentNotification } from '../lib/incidentSettings';
import { getUserSites, type Site } from '../lib/sites';
import { uploadIncidentAttachment } from '../lib/attachments';
import type { Incident } from '../lib/incidents';
import { captureException } from '../lib/sentry';

interface UseIncidentFormOptions {
  isOpen: boolean;
  onSuccess: () => void;
}

interface UseIncidentFormReturn {
  selectedCategory: Incident['category'] | null;
  setSelectedCategory: (category: Incident['category'] | null) => void;
  description: string;
  setDescription: (value: string) => void;
  isDescriptionFocused: boolean;
  setIsDescriptionFocused: (focused: boolean) => void;
  userSites: Site[];
  selectedSiteId: string;
  setSelectedSiteId: (id: string) => void;
  attachedFiles: File[];
  setAttachedFiles: (files: File[]) => void;
  isLoadingSites: boolean;
  isSubmitting: boolean;
  error: string;
  canSubmit: boolean;
  isDirty: boolean;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  resetForm: () => void;
}

const MAX_DESCRIPTION_LENGTH = 500;

export function useIncidentForm({ isOpen, onSuccess }: UseIncidentFormOptions): UseIncidentFormReturn {
  const { user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState<Incident['category'] | null>(null);
  const [description, setDescription] = useState('');
  const [isDescriptionFocused, setIsDescriptionFocused] = useState(false);
  const [userSites, setUserSites] = useState<Site[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Track if form has any changes
  // For multi-site users, also consider site selection as a change
  // Also consider file attachments
  const isDirty = description.trim() !== '' || selectedCategory !== null || (userSites.length > 1 && selectedSiteId !== '') || attachedFiles.length > 0;

  // Load user's sites when form opens
  useEffect(() => {
    if (isOpen && user?.id) {
      setIsLoadingSites(true);

      const timeoutId = setTimeout(() => {
        if (isLoadingSites) {
          setIsLoadingSites(false);
        }
      }, 5000);

      getUserSites(user.id).then((result) => {
        clearTimeout(timeoutId);
        if (result.success && result.sites) {
          setUserSites(result.sites);
          if (result.sites.length === 1) {
            setSelectedSiteId(result.sites[0].id);
          }
        }
        setIsLoadingSites(false);
      });

      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, user?.id]);

  // Reset form when opened
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setSelectedCategory(null);
    setDescription('');
    setError('');
    setSelectedSiteId('');
    setIsDescriptionFocused(false);
    setAttachedFiles([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!description.trim()) {
      setError('Veuillez décrire le problème');
      return;
    }

    if (description.length > MAX_DESCRIPTION_LENGTH) {
      setError(`La description ne peut pas dépasser ${MAX_DESCRIPTION_LENGTH} caractères`);
      return;
    }

    if (!selectedSiteId) {
      setError('Veuillez sélectionner un site');
      return;
    }

    setIsSubmitting(true);

    try {
      // Step 1: Create the incident first
      const result = await createIncident(
        selectedCategory,
        description,
        selectedSiteId
      );

      if (!result.success || !result.data) {
        setError(result.error || 'Une erreur est survenue');
        setIsSubmitting(false);
        return;
      }

      const incidentId = result.data.id;

      // Step 2: Upload attachments if any
      if (attachedFiles.length > 0) {
        const uploadPromises = attachedFiles.map(file =>
          uploadIncidentAttachment(incidentId, file).catch(error => {
            if (import.meta.env.DEV) console.error('Error uploading attachment:', error);
            captureException(error, { context: 'useIncidentForm.uploadAttachment', incidentId });
            return null;
          })
        );

        await Promise.all(uploadPromises);
      }

      // Fire-and-forget: send email notification (does not block success flow)
      sendIncidentNotification(incidentId);

      // Success - reset form and call success callback
      resetForm();
      onSuccess();
    } catch (err) {
      if (import.meta.env.DEV) console.error('Error submitting incident:', err);
      captureException(err, { context: 'useIncidentForm.handleSubmit' });
      setError('Une erreur est survenue lors de l\'envoi');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = description.trim() !== '' && selectedSiteId !== '' && !isSubmitting;

  return {
    selectedCategory,
    setSelectedCategory,
    description,
    setDescription,
    isDescriptionFocused,
    setIsDescriptionFocused,
    userSites,
    selectedSiteId,
    setSelectedSiteId,
    attachedFiles,
    setAttachedFiles,
    isLoadingSites,
    isSubmitting,
    error,
    canSubmit,
    isDirty,
    handleSubmit,
    resetForm,
  };
}

export { MAX_DESCRIPTION_LENGTH };
